import CardCommon from '@op/components/CardCommon';
import { cardIcon, tableIcon } from '@op/constants/chart';
import React, { useState } from 'react';
import FigureCard from '@op/pages/Overview/TrendingPublications/components/FigureCard';
import SvgIcon from '@/pages/home/components/SvgIcon';
import useLanguage from '@op/hooks/useLanguage';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import TableCommonHook from '@op/components/TableCommonHook';
import { useTrendingColumn } from '@op/components/TableCommonHook/hooks/useTrendingColumn';
import { SortBy } from 'tea-component/lib/table/addons';
import { useMultiGamesTrendingRequest } from '../hooks/useMultiGamesTrendingRequest';
import type { TGameMap } from '../hooks/useMultiGamesCond';
import '@op/pages/Overview/TrendingPublications/TrendingVideo/index.scss';
import '../index.scss';

import { LanguageType } from '@op/types/common';
import { goToFeedsReviewsDetail } from '@op/utils/url';
import { useHistory } from 'umi';

const sortByOptions = [
  { value: 'view', text: 'Views' },
  { value: 'engagement', text: 'Engagement' },
  { value: 'follower_number', text: 'Followers' },
  { value: 'tweets_reply', text: 'Comments' },
  { value: 'comment_time', text: 'Date' },
];

const sortColumns = sortByOptions.map((val) => val.value);

const extraFilter = {
  review_types: ['posts_video', 'stream_titles'],
  media_types: ['video', 'live'],
};

interface IZoneTrendingVideoProps {
  filter: any;
  gameMap: TGameMap;
  language: LanguageType;
  loadingHeight?: number;
  emptyHeight?: number;
}

export default function ZoneTrendingVideo({
  filter,
  gameMap,
  language,
  loadingHeight = 300,
  emptyHeight = 300,
}: IZoneTrendingVideoProps) {
  const { formatMessage } = useLanguage();
  const [isActive, setIsActive] = useState(false);
  const [view, setView] = useState('card');
  const [sortBy, setSortBy] = useState<SortBy<any>>({
    by: 'engagement',
    order: 'desc',
  });

  const { data, loading } = useMultiGamesTrendingRequest({
    filter,
    sortBy: { by: sortBy.by, order: sortBy.order || 'desc' },
    extraFilter,
  });

  const { columns } = useTrendingColumn({
    language,
    keys: ['video', 'authorCountry', 'view', 'engagement', 'subscribe', 'tweetsReply'],
    statistics: data?.statistics,
    logBtnId: 'TrendingVideos',
    gameMap,
  });

  const hasMore = (data?.total || 0) > 4;
  const history = useHistory();

  return (
    <CardCommon
      title={formatMessage({ id: 'Trending Videos' })}
      operations={[cardIcon, tableIcon]}
      loading={loading}
      className="card--trend-video"
      handleOperationsOnClick={(item) => {
        setView(item?.symbol || 'card');
      }}
      loadingHeight={loadingHeight}
      emptyHeight={emptyHeight}
      empty={data?.list?.length === 0}
    >
      <div className={`trend-video-list-wrapper ${isActive || !hasMore ? 'active' : ''}`}>
        {view === 'card' ? (
          <>
            <SortBySelect
              defaultParams={{
                options: sortByOptions,
                btnTextPrefix: formatMessage({ id: 'Sort By' }),
              }}
              defaultValue={sortBy.by}
              onChange={(_key: any, val: string) => {
                setSortBy({ by: val, order: 'desc' });
              }}
            />
            <div className="trend-video-list">
              {data?.list?.map((item: any, index: number) => {
                const game = gameMap.get(item.unified_id);
                return (
                  <div className="trend-video-item" key={item.comment_uin || index}>
                    <FigureCard
                      size="small"
                      item={item}
                      language={language}
                      direction="column"
                      navigatePage={(_e: any) => {
                        goToFeedsReviewsDetail(
                          history,
                          item,
                          game
                            ? { language, gameid: item.unified_id }
                            : { language },
                        );
                      }}
                      game={game}
                      showGame
                    />
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="trend-video-table">
            <TableCommonHook
              recordKey={'comment_uin'}
              records={data?.list || []}
              total={data?.total || 0}
              columns={columns}
              sortColumns={sortColumns}
              hasPageable={false}
              defaultSort={sortBy}
              sortInfo={[sortBy, setSortBy]}
              logBtnId={'TrendingVideos'}
            />
          </div>
        )}
        {hasMore && (
          <div className="dbo-switch-show" onClick={() => setIsActive(!isActive)}>
            {isActive ? (
              <>
                <span>{formatMessage({ id: 'Close-1' })}</span>
                <SvgIcon type="arrow-up" />
              </>
            ) : (
              <>
                <span>{formatMessage({ id: 'More' })}</span>
                <SvgIcon type="arrow-down" />
              </>
            )}
          </div>
        )}
      </div>
    </CardCommon>
  );
}
